import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiTopic } from '@wix/da-papi-types';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { BiData, ItemClickBiEvent } from '@wix/da-bi/pkg/events';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { FacetListRow } from '@wix/da-shared-react/pkg/FacetList';
import { FacetButton } from '@wix/da-ds/pkg/FacetList/Facet';

const BI_SECTIONNAME = 'topics_homepage';

export interface Props {
  topics?: PapiTopic[];
  topicFilter?: string;
  className?: string;
}

const TopicFilterSlider: React.FC<Props> = ({
  topics,
  topicFilter,
  className,
}) => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const selectedRef = useRef<HTMLElement | null>(null);

  // if a topic is selected, try to put it in the middle of the screen
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: 'instant' as any /* instant isn't in ts yet */,
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [isMounted]);

  if (!topics) {
    return null;
  }

  return (
    <FacetListRow
      className={className}
      facets={[
        <FacetButton
          size="large"
          key={`topic-all`}
          selected={!topicFilter}
          href={isMounted ? removeFromCurrentUrl('topic') : undefined}
          biData={BiData<ItemClickBiEvent>({
            evid: 100,
            sectionname: BI_SECTIONNAME,
            typeid: ResourceType.TOPIC,
          })}
        >
          {t('browse.topicFilter.all')}
        </FacetButton>,
        ...topics.map(topic => {
          const slug = slugifyPapiTopicName(topic);
          const isSelected =
            !!slug && slug === (topicFilter || '').toLowerCase();
          return (
            <FacetButton
              key={`topic-${topic.itemId}`}
              size="large"
              selected={isSelected}
              innerRef={node => {
                if (node && isSelected) {
                  selectedRef.current = node;
                }
              }}
              href={
                !!slug && isMounted ? addToCurrentUrl('topic', slug) : undefined
              }
              biData={BiData<ItemClickBiEvent>({
                evid: 100,
                sectionname: BI_SECTIONNAME,
                typeid: ResourceType.TOPIC,
                itemid: topic.itemId,
              })}
            >
              {topic.name}
            </FacetButton>
          );
        }),
      ]}
    />
  );
};
TopicFilterSlider.displayName = 'TopicFilterSlider';

export default TopicFilterSlider;

/** get the 'canonical name' of a topic (like 'anime-and-manga') from a PapiTopic */
function slugifyPapiTopicName(papiTopic: PapiTopic): string | undefined {
  return papiTopic.url?.replace(/^.*topic=([\w-]+)&{0,1}.*$/, '$1');
}

function removeFromCurrentUrl(key: string): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const url = new URL(window.location.toString());
  url.searchParams.delete(key);
  return url.toString();
}

function addToCurrentUrl(key: string, value: string): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const url = new URL(window.location.toString());
  url.searchParams.set(key, value);
  return url.toString();
}
